/*
 This class, once activated will return the amount of time that the tab is in focus.
 NOTE: this class uses the HTML5 Page Visibility API. On browsers that don't support
       the api, the total time is used.
 We take into account:
    - page visibility (switching between browser tabs)
    - window focus/blur (alt-tab out of browser/switching to another app)
    - user activity (mouse/touch/keyboard) to detect idle
*/

const vendorEvents = [
    { hidden: 'hidden', event: 'visibilitychange' },
    { hidden: 'webkitHidden', event: 'webkitvisibilitychange' },
    { hidden: 'mozHidden', event: 'mozvisibilitychange' },
    { hidden: 'msHidden', event: 'msvisibilitychange' },
    { hidden: 'oHidden', event: 'ovisibilitychange' },
];

// threshold for how many milliseconds users we'll pause the timer if the
// user doesn't do anything (mouse, keyboard, etc)
const IDLE_THRESHOLD_MS = 60 * 1000; // 1 minute

export default class TimeTracker {
    private totalAccumulated: number;
    private intervalStart: number;
    private isCounting: boolean;
    private isVisible: boolean;
    private isActive: boolean;
    private idleTimer: number | null = null;

    constructor(initialSeconds = 0) {
        this.totalAccumulated = initialSeconds;
        this.intervalStart = TimeTracker.now();
        this.isCounting = true;
        this.isVisible = true;
        this.isActive = true;

        // 1) Page‐visibility events
        const viz = this.getVisibilityProps();
        if (viz) {
            document.addEventListener(viz.event, () => this.handleVisibility());
        }

        // 2) Window blur/focus (catches Alt-Tab / app switch)
        window.addEventListener('blur', () => this.handleVisibility());
        window.addEventListener('focus', () => this.handleVisibility());

        // 3) User activity → reset idle timeout
        ['mousemove', 'keydown', 'touchstart'].forEach((evt) =>
            window.addEventListener(evt, () => this.handleActivity(), true),
        );

        this.resetIdleTimer();
    }

    /** Zero everything out */
    clear() {
        this.totalAccumulated = 0;
        this.intervalStart = TimeTracker.now();
        this.isCounting = true;
        this.isVisible = true;
        this.isActive = true;
        this.resetIdleTimer();
    }

    /** How many whole seconds has the user been “visible & active”? */
    getSecondsPassed(): number {
        let secs = this.totalAccumulated;
        if (this.isCounting) {
            secs += TimeTracker.now() - this.intervalStart;
        }
        return Math.floor(secs);
    }

    /** Called on visibilitychange / blur / focus */
    private handleVisibility() {
        // 1) page hidden?
        const viz = this.getVisibilityProps();
        const isHidden = viz ? Boolean((document as any)[viz.hidden]) : false;
        // 2) window still focused?
        const hasFocus = document.hasFocus();

        this.isVisible = !isHidden && hasFocus;
        this.updateCounting();
    }

    /** Called on any user input to detect end of “idle” */
    private handleActivity() {
        if (!this.isActive) {
            this.isActive = true;
            this.updateCounting();
        }
        this.resetIdleTimer();
    }

    /** Fired when idle threshold is reached */
    private onIdle() {
        this.isActive = false;
        this.updateCounting();
    }

    /** (Re)start the 60 s idle‐timeout */
    private resetIdleTimer() {
        if (this.idleTimer !== null) {
            clearTimeout(this.idleTimer);
        }
        this.idleTimer = window.setTimeout(() => this.onIdle(), IDLE_THRESHOLD_MS);
    }

    /** Central start/stop logic */
    private updateCounting() {
        const shouldCount = this.isVisible && this.isActive;

        if (shouldCount && !this.isCounting) {
            // resume
            this.intervalStart = TimeTracker.now();
        } else if (!shouldCount && this.isCounting) {
            // pause
            const elapsed = TimeTracker.now() - this.intervalStart;
            this.totalAccumulated += elapsed;
        }

        this.isCounting = shouldCount;
    }

    /** Detect which vendor prefix (if any) the browser uses */
    private getVisibilityProps(): { hidden: string; event: string } | null {
        for (const ve of vendorEvents) {
            if (ve.hidden in document) {
                return ve;
            }
        }
        return null;
    }

    private static now(): number {
        return Date.now() / 1000;
    }
}
